import Toastify from 'toastify-js';
import 'toastify-js/src/toastify.css';

export function orderForm() {
  // State management
  const state = {
    productData: { products: {}, attribute_options: {} },
    selectedAttributes: {},
    selectedProductId: null,
    selectedVariantIndex: null,
    activeFilter: '*',
  };

  // Cache DOM elements
  const elements = {
    block: document.querySelector('.wp-block-order-form'),
    form: document.querySelector('.wp-block-order-form form'),
    step1: document.querySelector('.step[data-step="1"]'),
    notesStep: document.querySelector('.step[data-step="notes"]'),
    infoStep: document.querySelector('.step[data-step="info"]'),
    productItems: document.querySelectorAll('.step[data-step="1"] li'),
    filterButtons: document.querySelectorAll('#product-filters button'),
    toggleButton: document.getElementById('toggle-all-products'),
  };

  // Derived elements
  elements.submitButton = elements.form?.querySelector('[type="submit"]');
  elements.productButtons = elements.form?.querySelectorAll('.product-choice');

  // Initialize the form
  init();

  function init() {
    if (!elements.form) return;

    bindEvents();
    fetchProductData();
  }

  function bindEvents() {
    // Product selection
    elements.productButtons?.forEach((button) => {
      button.addEventListener('click', handleProductSelection);
    });

    // Toggle extra products
    if (elements.toggleButton && elements.step1) {
      elements.toggleButton.addEventListener('click', toggleExtraProducts);
    }

    // Form submission
    elements.form?.addEventListener('submit', handleFormSubmit);

    // Initialize product filters
    if (elements.filterButtons?.length) {
      elements.filterButtons.forEach((button) => {
        button.addEventListener('click', handleFilterClick);
      });
    }
  }

  function handleProductSelection() {
    const isActive = this.getAttribute('aria-pressed') === 'true';
    const productId = this.getAttribute('data-product-id');
    const parentLi = this.closest('li');

    resetSelections();

    if (!isActive) {
      selectProduct(this, parentLi);
      generateAllAttributeSteps(productId);
    } else {
      deselectProduct(this, parentLi);
    }
  }

  function handleFilterClick() {
    const wasActive = this.getAttribute('aria-pressed') === 'true';
    const filter = this.getAttribute('data-filter');

    // Reset all buttons
    elements.filterButtons.forEach((btn) =>
      btn.setAttribute('aria-pressed', 'false'),
    );

    // If the button was already active, clear filter
    if (wasActive) {
      state.activeFilter = '*';
    } else {
      this.setAttribute('aria-pressed', 'true');
      state.activeFilter = filter;
    }

    applyFilter(state.activeFilter);
  }

  function toggleExtraProducts() {
    const isExpanded = elements.step1.classList.toggle('expanded');
    const extraProducts = elements.step1.querySelectorAll('.show-more');

    extraProducts.forEach((el) => el.classList.toggle('hidden', !isExpanded));

    // Toggle text visibility
    const showMoreText = elements.toggleButton.querySelector('.show-more-text');
    const showLessText = elements.toggleButton.querySelector('.show-less-text');
    showMoreText.classList.toggle('hidden', isExpanded);
    showLessText.classList.toggle('hidden', !isExpanded);

    // Update aria-label
    elements.toggleButton.setAttribute(
      'aria-label',
      elements.toggleButton
        .querySelector('span:not(.hidden)')
        .textContent.trim(),
    );
  }

  async function fetchProductData() {
    try {
      elements.block?.classList.add('loading');
      const configUrl = window.js_localizations?.configurations_url;
      const response = await fetch(configUrl);

      if (!response.ok)
        throw new Error(`API request failed: ${response.status}`);

      state.productData = await response.json();
      handleUrlParameters();
      initializeProductFilters();
    } catch (error) {
      console.error('Error fetching product configurations:', error);
    } finally {
      elements.block?.classList.remove('loading');
    }
  }

  function parseUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const parsedParams = {
      productId: urlParams.get('product_id')
        ? parseInt(urlParams.get('product_id'))
        : null,
      attributes: {},
    };

    // Parse attribute parameters (format: attribute_X=Y)
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('attribute_')) {
        parsedParams.attributes[key.replace('attribute_', '')] = value;
      }
    }

    return parsedParams;
  }

  async function handleUrlParameters() {
    const params = parseUrlParams();
    if (!params.productId) return;

    // Find and select the product
    const productButton = elements.form.querySelector(
      `.product-choice[data-product-id="${params.productId}"]`,
    );
    if (!productButton) return;

    const productLi = productButton.closest('li');
    const parentElement = productLi.parentElement;
    const relatedProductFilterButton = elements.form.querySelector(
      `button[data-filter="${productLi.dataset.productType}"]`,
    );

    // Automatically filter on selected product's product type (request from Ronald)
    await new Promise((resolve) => setTimeout(resolve, 200));
    relatedProductFilterButton.click();

    // Move product to front
    parentElement.insertBefore(productLi, parentElement.firstChild);

    // Handle show-more class
    if (productLi.classList.contains('show-more')) {
      productLi.classList.remove('show-more', 'hidden');

      // Find visible products and adjust show-more status
      const visibleProducts = Array.from(
        parentElement.querySelectorAll('li:not(.show-more):not(.hidden)'),
      );
      if (visibleProducts.length > 0) {
        const lastVisibleProduct = visibleProducts[visibleProducts.length - 1];
        if (lastVisibleProduct !== productLi) {
          lastVisibleProduct.classList.add('show-more', 'hidden');
        }
      }
    }

    // Important fix: Store all URL attribute selections first before applying them
    // This prevents partial filtering before all selections are made
    const attributeSelections = Object.entries(params.attributes);

    // Select the product first without filtering options
    productButton.click();

    // Prepare all attribute selections at once before applying them
    const attrParams = {};
    for (const [attributeId, optionId] of attributeSelections) {
      attrParams[attributeId] = optionId;
    }

    // Set state directly without filtering between selections
    state.selectedAttributes = attrParams;

    // Now apply the visual state to all buttons
    for (const [attributeId, optionId] of attributeSelections) {
      const step = elements.form.querySelector(
        `.step[data-attribute-id="${attributeId}"]`,
      );
      if (!step) continue;

      const optionButton = step.querySelector(
        `.option-select[data-option-id="${optionId}"]`,
      );
      if (optionButton) {
        // Set visual state without triggering click handler
        optionButton.setAttribute('aria-pressed', 'true');
        optionButton.querySelector('div')?.classList.add('active');

        // Enable the step if it's not already enabled
        step.removeAttribute('inert');
      }
    }

    // Update the entire form state at once after all selections
    updateSelectedVariant(params.productId);

    // Enable the last step if all attributes are selected
    if (isAllAttributesSelected() && elements.infoStep && elements.notesStep) {
      elements.notesStep.removeAttribute('inert');
      elements.infoStep.removeAttribute('inert');
    }
  }

  function resetSelections() {
    // Clear selected state
    state.selectedAttributes = {};
    state.selectedProductId = null;
    state.selectedVariantIndex = null;

    // Remove attribute steps
    const attributeSteps = elements.form.querySelectorAll('.step[data-step]');
    attributeSteps.forEach((step) => {
      if (parseInt(step.dataset.step) > 1 && step.dataset.step !== 'last') {
        step.remove();
      }
    });

    // Hide info & notes step
    if (elements.infoStep && elements.notesStep) {
      elements.notesStep.setAttribute('inert', '');
      elements.infoStep.setAttribute('inert', '');
    }
  }

  function selectProduct(button, parentLi) {
    // Reset previous selections
    elements.productButtons.forEach((btn) => {
      btn.setAttribute('aria-pressed', 'false');
      btn.classList.remove('active');
      btn.closest('li')?.classList.remove('active');
    });

    // Mark as selected
    button.setAttribute('aria-pressed', 'true');
    button.classList.add('active');
    parentLi.classList.add('active');
    state.selectedProductId =
      parseInt(button.getAttribute('data-product-id')) || null;
  }

  function deselectProduct(button, parentLi) {
    button.setAttribute('aria-pressed', 'false');
    button.classList.remove('active');
    parentLi.classList.remove('active');
    state.selectedProductId = null;
    state.selectedVariantIndex = null;
    document.getElementById('total-price').classList.add('hidden');
  }

  function handleFormSubmit(event) {
    event.preventDefault();
    elements.block.classList.add('loading');
    elements.submitButton.setAttribute('aria-pressed', 'true');

    const formData = new FormData(elements.form);
    formData.append('action', 'order_submit');

    // Add product and attribute data
    if (state.selectedProductId) {
      formData.append('product_id', state.selectedProductId);
    }

    Object.entries(state.selectedAttributes).forEach(
      ([attributeId, optionId]) => {
        formData.append(`attribute_${attributeId}`, optionId);
      },
    );

    if (state.selectedVariantIndex != null) {
      formData.append('variant_index', state.selectedVariantIndex);
    }

    fetch(js_localizations.ajax_url, {
      method: 'POST',
      body: formData,
    })
      .then((response) => response.json())
      .then(handleFormResponse)
      .catch(handleFormError)
      .finally(() => {
        elements.block.classList.remove('loading');
        elements.submitButton.setAttribute('aria-pressed', 'false');
      });
  }

  // Function to generate all attribute steps at once
  function generateAllAttributeSteps(productId) {
    // Wait for data to be loaded
    if (
      !state.productData.products ||
      Object.keys(state.productData.products).length === 0
    ) {
      setTimeout(() => generateAllAttributeSteps(productId), 100);
      return;
    }

    const product = state.productData.products[productId];
    if (!product) {
      if (elements.infoStep && elements.notesStep) {
        elements.notesStep.removeAttribute('inert');
        elements.infoStep.removeAttribute('inert');
      }
      return;
    }

    // Create all attribute steps if available
    if (product.attributes.length > 0) {
      product.attributes.forEach((attributeId, index) => {
        const stepNumber = index + 2; // Steps start at 2 (after product selection)

        // For the first step, get all options
        // For subsequent steps, initially show all options but set them as disabled except for the first attribute
        const options =
          index === 0
            ? getFilteredAttributeOptions(attributeId, product, {})
            : getAttributeOptions(attributeId, product);

        const step = createAttributeStep(
          stepNumber,
          attributeId,
          product.attribute_labels[attributeId],
          product.attribute_descriptions[attributeId],
          options,
          productId,
          index > 0, // Set inert for all steps except the first attribute step
        );

        // Insert step before last step or append to form
        if (elements.notesStep) {
          elements.form.insertBefore(step, elements.notesStep);
        } else {
          elements.form.appendChild(step);
        }
      });
    }

    // Show the final step
    if (elements.infoStep && elements.notesStep) {
      elements.notesStep.setAttribute('inert', '');
      elements.infoStep.setAttribute('inert', '');
    }
  }

  // Get all possible options for an attribute without filtering
  function getAttributeOptions(attributeId, product) {
    const optionIds = new Set();

    // Get all possible options for this attribute
    product.variations.forEach((variation) => {
      if (variation[attributeId] !== undefined) {
        optionIds.add(variation[attributeId]);
      }
    });

    // Map to full option data
    return Array.from(optionIds).map((optionId) => {
      const option = state.productData.attribute_options[optionId] || {
        name: `Option ${optionId}`,
        has_display: false,
      };
      return { id: optionId, ...option };
    });
  }

  function getFilteredAttributeOptions(
    attributeId,
    product,
    currentSelections,
  ) {
    // Get matching variations
    const matchingVariations = product.variations.filter((variation) =>
      Object.entries(currentSelections).every(
        ([attrId, optionId]) => variation[attrId] == optionId,
      ),
    );

    // Get unique option IDs
    const optionIds = new Set();
    matchingVariations.forEach((variation) => {
      if (variation[attributeId] !== undefined) {
        optionIds.add(variation[attributeId]);
      }
    });

    // Map to full option data
    return Array.from(optionIds).map((optionId) => {
      const option = state.productData.attribute_options[optionId] || {
        name: `Option ${optionId}`,
        has_display: false,
      };
      return { id: optionId, ...option };
    });
  }

  function createAttributeStep(
    stepNumber,
    attributeId,
    attributeName,
    attributeDescription,
    options,
    productId,
    setInert = false,
  ) {
    const step = document.createElement('div');
    step.className = 'step rounded-lg border border-black p-5';
    step.dataset.step = stepNumber;
    step.dataset.attributeId = attributeId;
    step.dataset.productId = productId;

    if (setInert) {
      step.setAttribute('inert', '');
    }

    // Create heading
    const heading = document.createElement('div');
    heading.className =
      'mb-7 flex flex-col gap-2 text-lg leading-[160%] font-semibold md:flex-row md:items-center md:justify-between';

    const title = document.createElement('h2');
    title.className = '!text-lg !leading-[150%] !font-medium';
    title.textContent = attributeName;

    heading.appendChild(title);

    if (attributeDescription) {
      const description = document.createElement('p');
      description.className = 'font-sans-2 font-medium';
      description.textContent = attributeDescription;
      heading.appendChild(description);
    }

    step.appendChild(heading);

    // Create options list
    const optionsList = document.createElement('ol');
    optionsList.className =
      'grid grid-cols-2 items-center gap-[30px] lg:grid-cols-3';

    // Add options
    options.forEach((option) => {
      const listItem = document.createElement('li');
      listItem.className = 'space-y-2.5';

      const button = document.createElement('button');
      button.className =
        'group flex flex-col option-select w-full cursor-pointer relative rounded-lg transition-opacity';
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('type', 'button');

      // Check if option is sold out
      const isSoldOut = !state.productData.products[productId].variations.some(
        (variation) =>
          variation[attributeId] == option.id &&
          variation.in_assortment === true,
      );

      if (isSoldOut) {
        button.classList.add('disabled:cursor-not-allowed');
        button.setAttribute('disabled', 'true');
        button.setAttribute(
          'aria-label',
          `${option.name} - ${window.js_localizations.sold_out_label}`,
        );

        // Add sold out indicator
        const soldOutLine = document.createElement('div');
        soldOutLine.className = 'absolute inset-0 pointer-events-none z-10';
        soldOutLine.innerHTML = `
          <svg class="absolute inset-0 w-full h-full opacity-50" preserveAspectRatio="none">
            <line x1="0" y1="100%" x2="100%" y1="0" stroke="red" stroke-width="3" />
          </svg>
        `;
        button.appendChild(soldOutLine);
      } else {
        button.setAttribute('data-option-id', option.id);
        button.setAttribute(
          'aria-label',
          `${window.js_localizations.select_label} ${option.name}`,
        );
      }

      // Add option selection event listener
      button.addEventListener('click', function () {
        if (this.classList.contains('sold-out')) return;

        const currentStep = this.closest('.step');
        const currentAttributeId = currentStep.dataset.attributeId;
        const optionId = this.dataset.optionId;
        const productId = currentStep.dataset.productId;
        const currentStepNumber = parseInt(currentStep.dataset.step);
        const isAlreadySelected = this.getAttribute('aria-pressed') === 'true';

        if (isAlreadySelected) {
          // Deselect option
          this.setAttribute('aria-pressed', 'false');
          this.querySelector('div').classList.remove('active');
          delete state.selectedAttributes[currentAttributeId];

          // Make subsequent steps inert
          makeSubsequentStepsInert(currentStepNumber);
          updateSubsequentStepsOptions(currentStepNumber, productId);
        } else {
          // Check if a different option is already selected in this step
          const hasExistingSelection =
            currentAttributeId in state.selectedAttributes;

          // Select this option
          currentStep.querySelectorAll('.option-select').forEach((btn) => {
            btn.setAttribute('aria-pressed', 'false');
            btn.querySelector('div')?.classList.remove('active');
          });

          this.setAttribute('aria-pressed', 'true');
          this.querySelector('div').classList.add('active');
          state.selectedAttributes[currentAttributeId] = optionId;

          // If we're changing a selection (not just making a new one),
          // deselect all subsequent steps as well
          if (hasExistingSelection) {
            makeSubsequentStepsInert(currentStepNumber);
          }

          // Enable the next step if it exists
          enableNextStep(currentStepNumber);

          // Update options in subsequent steps based on current selections
          updateSubsequentStepsOptions(currentStepNumber, productId);
        }

        updateSelectedVariant(productId);
      });

      // Create option content container
      const container = document.createElement('div');
      container.className =
        'thumbnail-container relative w-full overflow-hidden flex items-center justify-center border-gray-7 group-aria-pressed:border-gray-8 group-hover:border-gray-8 rounded-lg border p-[5px] transition-colors group-aria-pressed:border-2';

      const optionName = document.createElement('p');
      optionName.className =
        'text-gray-8/60 group-aria-pressed:text-gray-8 group-hover:text-gray-8 mt-2.5 text-left leading-[160%] font-medium transition-colors';
      optionName.textContent = option.name;

      if (isSoldOut) {
        container.classList.add('pointer-events-none');
        container.classList.remove('group-hover:border-gray-8');
        optionName.classList.remove('group-hover:text-gray-8');
      }

      // Add display content based on option type
      if (option.has_display) {
        optionName.className +=
          ' underline-animation after:bg-gray-8 self-start';

        if (option.display_type === 'image' && option.image) {
          const img = document.createElement('img');
          img.src = option.image;
          img.className = 'aspect-[2.2/1.3] w-full h-auto rounded-lg';
          img.alt = option.name;

          button.appendChild(container);
          button.appendChild(optionName);
          container.appendChild(img);
        } else if (option.display_type === 'color' && option.color) {
          const colorDiv = document.createElement('div');
          colorDiv.className = 'aspect-[2.2/1.3] size-full rounded-lg';
          colorDiv.style.backgroundColor = option.color;

          button.appendChild(container);
          button.appendChild(optionName);
          container.appendChild(colorDiv);
        }
      } else {
        optionName.classList.remove('mt-2.5');
        optionName.classList.add('p-5');
        button.appendChild(container);
        container.appendChild(optionName);
      }

      listItem.appendChild(button);
      optionsList.appendChild(listItem);
    });

    step.appendChild(optionsList);
    return step;
  }

  // Enable the next step when the current step has a selection
  function enableNextStep(currentStepNumber) {
    const nextStep = elements.form.querySelector(
      `.step[data-step="${currentStepNumber + 1}"]`,
    );
    if (nextStep) {
      nextStep.removeAttribute('inert');
    } else if (
      elements.infoStep &&
      elements.notesStep &&
      isAllAttributesSelected()
    ) {
      elements.notesStep.removeAttribute('inert');
      elements.infoStep.removeAttribute('inert');
    }
  }

  // Make all subsequent steps inert when a previous step's selection is removed
  function makeSubsequentStepsInert(currentStepNumber) {
    const steps = elements.form.querySelectorAll('.step[data-step]');
    steps.forEach((step) => {
      const stepNumber = parseInt(step.dataset.step);
      if (stepNumber > currentStepNumber && step.dataset.step !== 'last') {
        step.setAttribute('inert', '');

        // Unselect all options in this step
        const optionButtons = step.querySelectorAll('.option-select');
        optionButtons.forEach((button) => {
          button.setAttribute('aria-pressed', 'false');
          button.querySelector('div')?.classList.remove('active');

          // Add visual indication that the selection was cleared
          const container = button.querySelector('.thumbnail-container');
          if (container) {
            container.classList.remove('border-2');
          }
        });

        // Remove the attribute from state.selectedAttributes
        const attributeId = step.dataset.attributeId;
        if (attributeId && attributeId in state.selectedAttributes) {
          delete state.selectedAttributes[attributeId];
        }
      }
    });

    // Always make the info and notes step inert when deselecting an option
    if (elements.infoStep && elements.notesStep) {
      elements.notesStep.setAttribute('inert', '');
      elements.infoStep.setAttribute('inert', '');
    }
  }

  // Check if all attributes have been selected
  function isAllAttributesSelected() {
    const product = state.productData.products[state.selectedProductId];
    if (!product) return false;

    return (
      product.attributes.length === Object.keys(state.selectedAttributes).length
    );
  }

  // Update options in subsequent steps based on current selections
  // Update options in subsequent steps based on current selections
  function updateSubsequentStepsOptions(currentStepNumber, productId) {
    const product = state.productData.products[productId];
    if (!product) return;

    // Update all steps after the current one
    for (
      let stepNumber = currentStepNumber + 1;
      stepNumber < currentStepNumber + product.attributes.length;
      stepNumber++
    ) {
      const attributeIndex = stepNumber - 2;
      if (attributeIndex >= product.attributes.length) continue;

      const attributeId = product.attributes[attributeIndex];
      const step = elements.form.querySelector(
        `.step[data-step="${stepNumber}"]`,
      );

      if (step) {
        const options = getFilteredAttributeOptions(
          attributeId,
          product,
          state.selectedAttributes,
        );

        // Check if there's a selected option in this step
        const selectedButton = step.querySelector(
          '.option-select[aria-pressed="true"]',
        );
        let needToDeselect = false;

        if (selectedButton) {
          const selectedOptionId = selectedButton.dataset.optionId;
          // Check if the selected option is still valid
          const isSelectedOptionValid = options.some(
            (option) => option.id == selectedOptionId,
          );

          if (!isSelectedOptionValid) {
            // Deselect this option since it's no longer valid
            selectedButton.setAttribute('aria-pressed', 'false');
            selectedButton.querySelector('div')?.classList.remove('active');
            delete state.selectedAttributes[attributeId];
            needToDeselect = true;
          }
        }

        // If we deselected an option in this step, make all subsequent steps inert
        if (needToDeselect) {
          makeSubsequentStepsInert(stepNumber);
          break; // Exit the loop as we've handled all necessary steps
        }

        // Update each option in this step
        const optionButtons = step.querySelectorAll('.option-select');
        optionButtons.forEach((button) => {
          const optionId = button.dataset.optionId;

          // Check if this option is valid with current selections
          const isValidOption = options.some((option) => option.id == optionId);

          if (!isValidOption) {
            button.setAttribute('disabled', 'true');
            button.classList.add(
              'disabled:cursor-not-allowed',
              'disabled:opacity-50',
            );

            // Add sold out indicator if it doesn't exist
            if (!button.querySelector('.pointer-events-none')) {
              const soldOutLine = document.createElement('div');
              soldOutLine.className =
                'sold-out-line absolute inset-0 pointer-events-none z-10';
              soldOutLine.innerHTML = `
              <svg class="absolute inset-0 w-full h-full opacity-50" preserveAspectRatio="none">
                <line x1="0" y1="100%" x2="100%" y1="0" stroke="red" stroke-width="3" />
              </svg>
            `;

              if (button.querySelector('img')) {
                button
                  .querySelector('.thumbnail-container')
                  .appendChild(soldOutLine);
              } else {
                button.appendChild(soldOutLine);
              }
            }
          } else {
            button.removeAttribute('disabled');
            button.classList.remove('disabled:cursor-not-allowed');

            // Remove sold out indicator if it exists
            const soldOutLine = button.querySelector('.sold-out-line');
            if (soldOutLine) soldOutLine.remove();
          }
        });
      }
    }
  }

  function updateSelectedVariant(productId) {
    const product = state.productData.products[productId];
    if (!product) return;

    const totalPriceElement = document.getElementById('total-price');
    const priceSpan = totalPriceElement?.querySelector('span');

    if (
      Object.keys(state.selectedAttributes).length === product.attributes.length
    ) {
      const matchingVariantIndex = product.variations.findIndex((variation) =>
        Object.entries(state.selectedAttributes).every(
          ([attrId, optionId]) => variation[attrId] == optionId,
        ),
      );

      state.selectedVariantIndex =
        matchingVariantIndex === -1 ? null : matchingVariantIndex;

      // Enable the info and notes step if all attributes are selected and a variant is found
      if (
        state.selectedVariantIndex !== null &&
        elements.infoStep &&
        elements.notesStep
      ) {
        elements.notesStep.removeAttribute('inert');
        elements.infoStep.removeAttribute('inert');

        // Require order notes if a "custom" (in Dutch "maatwerk") configuration has been selected
        if (product.variations[state.selectedVariantIndex].custom === true) {
          showToast(window.js_localizations.custom_config_message, 7500);

          elements.form
            .querySelector('#order-notes')
            .setAttribute('required', 'true');
        } else {
          elements.form
            .querySelector('#order-notes')
            .removeAttribute('required');
        }

        // Show and update price if variant has a price
        if (totalPriceElement && priceSpan) {
          const selectedVariant =
            product.variations[state.selectedVariantIndex];

          if (selectedVariant.price !== undefined) {
            // Format the price with Euro symbol and comma for decimal
            const formattedPrice =
              '€ ' +
              selectedVariant.price.toLocaleString('nl-NL', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }) +
              ',-';

            priceSpan.textContent = formattedPrice;
            totalPriceElement.classList.remove('hidden');
          } else {
            totalPriceElement.classList.add('hidden');
          }
        }
      }
    } else {
      state.selectedVariantIndex = null;

      // Hide price when no variant is fully selected
      if (totalPriceElement) {
        totalPriceElement.classList.add('hidden');
      }
    }
  }

  function handleFormResponse(response) {
    if (response.success) {
      showToast(response.data.message || 'Success', -1);
      elements.form.reset();
      resetSelections();

      // Scroll to form
      const header = document.getElementById('site-header');
      const adminBar = document.getElementById('wpadminbar');
      let offset = (header?.offsetHeight || 0) + window.pageYOffset;
      if (adminBar) offset += adminBar.offsetHeight;

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } else {
      showToast(response.data.message || 'Error', -1);
    }
  }

  function handleFormError(error) {
    console.error('Error:', error);
    showToast(js_localizations.network_error_message, 5000);
  }

  function showToast(message, duration = 3000) {
    Toastify({
      text: message,
      duration: duration,
      close: true,
      gravity: 'top',
      position: 'center',
      stopOnFocus: true,
    }).showToast();
  }

  function initializeProductFilters() {
    // Apply initial filter
    applyFilter('*');

    // Handle toggle button clicks
    if (elements.toggleButton) {
      const originalClickHandler = elements.toggleButton.onclick;

      elements.toggleButton.onclick = function (e) {
        if (originalClickHandler) originalClickHandler.call(this, e);

        setTimeout(() => {
          applyFilter(state.activeFilter);
        }, 10);
      };
    }
  }

  function applyFilter(filterValue) {
    // Find selected product
    const selectedProductLi = elements.step1?.querySelector('li.active');
    const selectedProductButton =
      selectedProductLi?.querySelector('.product-choice');
    let shouldUnselectProduct = false;

    // Show/hide products based on filter
    elements.productItems.forEach((product) => {
      const productType = product.dataset.productType;

      if (filterValue === '*') {
        // Show all products but respect show-more limit
        if (
          product.classList.contains('show-more') &&
          !elements.step1.classList.contains('expanded')
        ) {
          product.classList.add('hidden');
        } else {
          product.classList.remove('hidden');
        }
      } else {
        // Show only products with matching data-product-type
        if (productType === filterValue) {
          product.classList.remove('hidden');
        } else {
          product.classList.add('hidden');
          // Check if this is the selected product
          if (product === selectedProductLi) {
            shouldUnselectProduct = true;
          }
        }
      }
    });

    // Unselect product if now hidden
    if (shouldUnselectProduct && selectedProductButton) {
      deselectProduct(selectedProductButton, selectedProductLi);
      resetSelections();
    }

    // Toggle visibility of "show more" button
    if (elements.toggleButton) {
      if (filterValue === '*') {
        elements.toggleButton.classList.remove('!hidden');
      } else {
        elements.toggleButton.classList.add('!hidden');
      }

      // Check if there are hidden items
      updateShowMoreButtonVisibility(filterValue);
    }
  }

  function updateShowMoreButtonVisibility(filterValue) {
    if (!elements.toggleButton) return;

    // Count filtered hidden items
    const filteredItems =
      filterValue === '*'
        ? elements.productItems
        : Array.from(elements.productItems).filter(
            (item) => item.dataset.productType == filterValue,
          );

    const hiddenFilteredItems = Array.from(filteredItems).filter(
      (item) =>
        item.classList.contains('show-more') &&
        item.classList.contains('hidden'),
    );

    // Show button only if there are hidden items
    elements.toggleButton.style.display =
      hiddenFilteredItems.length > 0 ? '' : 'none';
  }
}
